import React, { useEffect, useState } from 'react';
import { FiBook, FiEye, FiSearch, FiFileText } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { learnerPublicationAPI, learnerUtilityAPI } from '../../api/learnerEndpoints';
import { formatDate, formatFileSize } from '../../utils/helpers';
import {
  DashboardContainer,
  DashboardHeader,
  SectionTitle,
  ContentGrid,
  ContentCard,
  ContentCardHeader,
  ContentCardBody,
  ContentCardFooter,
  ContentActions,
  ActionButton,
  EmptyState,
  FilterBar,
  SearchInput,
  FilterSelect,
} from '../dashboards/Dashboard.styles';

const LearnerPublications = () => {
  const menuItems = getLearnerMenu();
  const [loading, setLoading] = useState(true);
  const [publications, setPublications] = useState([]);
  const [pagination, setPagination] = useState({});
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [selectedPublication, setSelectedPublication] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    grade_id: '',
    subject_id: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    per_page: 20,
  });

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    fetchPublications();
  }, [filters]);

  const fetchReferenceData = async () => {
    try {
      const [gradesRes, subjectsRes] = await Promise.all([
        learnerUtilityAPI.getGrades(),
        learnerUtilityAPI.getSubjects(),
      ]);
      setGrades(gradesRes.data?.grades || []);
      setSubjects(subjectsRes.data?.subjects || []);
    } catch (error) {
      showToast('error', 'Failed to load grades and subjects');
    }
  };

  const fetchPublications = async () => {
    try {
      setLoading(true);
      const response = await learnerPublicationAPI.getPublications(filters);
      setPublications(response.data?.publications || []);
      setPagination(response.data?.pagination || {});
    } catch (error) {
      showToast('error', 'Failed to load publications');
    } finally {
      setLoading(false);
    }
  };

  const openViewer = async (publication) => {
    setSelectedPublication(publication);
    setViewerOpen(true);
    setViewerLoading(true);
    setViewerUrl('');

    try {
      const response = await learnerPublicationAPI.getPreviewUrl(publication.id);
      const url = response.data?.url;
      if (url) {
        setViewerUrl(url);
      } else {
        setViewerUrl(learnerPublicationAPI.getStreamUrl(publication.id));
      }
    } catch (error) {
      setViewerUrl(learnerPublicationAPI.getStreamUrl(publication.id));
    } finally {
      setViewerLoading(false);
    }
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerUrl('');
    setSelectedPublication(null);
  };

  const getViewerIframeUrl = (url) => {
    if (!url) return '';
    const [base, hash] = url.split('#');
    const viewerFlags = 'toolbar=0&navpanes=0&scrollbar=0';
    return `${base}#${viewerFlags}${hash ? `&${hash}` : ''}`;
  };

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Publications Library</h1>
            <p>Browse publisher PDFs by grade and subject</p>
          </div>
          <Button
            variant="ghost"
            leftIcon={<FiSearch />}
            onClick={() => setFilters({ ...filters, search: '', grade_id: '', subject_id: '', page: 1 })}
          >
            Clear Filters
          </Button>
        </DashboardHeader>

        <FilterBar>
          <SearchInput
            placeholder="Search publications..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          />
          <FilterSelect
            value={filters.grade_id}
            onChange={(e) => setFilters({ ...filters, grade_id: e.target.value, page: 1 })}
          >
            <option value="">All Grades</option>
            {grades.map((grade) => (
              <option key={grade.id} value={grade.id}>{grade.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.subject_id}
            onChange={(e) => setFilters({ ...filters, subject_id: e.target.value, page: 1 })}
          >
            <option value="">All Subjects</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>{subject.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.sort_by}
            onChange={(e) => setFilters({ ...filters, sort_by: e.target.value, page: 1 })}
          >
            <option value="created_at">Sort by Date</option>
            <option value="title">Sort by Title</option>
            <option value="updated_at">Sort by Updated</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sort_order}
            onChange={(e) => setFilters({ ...filters, sort_order: e.target.value, page: 1 })}
          >
            <option value="desc">Newest</option>
            <option value="asc">Oldest</option>
          </FilterSelect>
        </FilterBar>

        <SectionTitle>Available Publications</SectionTitle>

        {loading ? (
          <LoadingSpinner size="large" text="Loading publications..." />
        ) : publications.length === 0 ? (
          <EmptyState>
            <FiFileText size={48} />
            <h3>No publications found</h3>
            <p>Try adjusting your filters or check back later.</p>
          </EmptyState>
        ) : (
          <ContentGrid>
            {publications.map((publication) => (
              <ContentCard key={publication.id}>
                <ContentCardHeader>
                  <div>
                    <h3>{publication.title}</h3>
                    <span className="badge" style={{ background: '#2c5f3420', color: '#2c5f34' }}>
                      PDF
                    </span>
                  </div>
                </ContentCardHeader>
                <ContentCardBody>
                  <p>
                    {(subjects.find(s => s.id === publication.subject_id)?.name || publication.subject?.name || 'Subject')}{' '}
                    /{' '}
                    {(grades.find(g => g.id === publication.grade_id)?.name || publication.grade?.name || 'Grade')}
                  </p>
                  <div className="meta">
                    <span>{formatFileSize(publication.file_size || 0)}</span>
                    <span>{formatDate(publication.created_at)}</span>
                  </div>
                </ContentCardBody>
                <ContentCardFooter>
                  <ContentActions>
                    <ActionButton title="View" onClick={() => openViewer(publication)}>
                      <FiEye />
                    </ActionButton>
                  </ContentActions>
                </ContentCardFooter>
              </ContentCard>
            ))}
          </ContentGrid>
        )}

        {pagination?.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
            <Button
              variant="ghost"
              disabled={pagination.page === 1}
              onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
            >
              Previous
            </Button>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px' }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="ghost"
              disabled={pagination.page === pagination.pages}
              onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
            >
              Next
            </Button>
          </div>
        )}

        <Modal
          isOpen={viewerOpen}
          onClose={closeViewer}
          title={selectedPublication?.title || 'Publication Preview'}
          size="full"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {viewerLoading ? (
              <LoadingSpinner size="large" text="Preparing preview..." />
            ) : viewerUrl ? (
              <div
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #E5E7EB',
                  width: '100%',
                  height: 'calc(100vh - 180px)',
                  minHeight: '60vh',
                }}
              >
                <iframe
                  src={getViewerIframeUrl(viewerUrl)}
                  title={selectedPublication?.title || 'Publication'}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              </div>
            ) : (
              <EmptyState>
                <FiBook size={48} />
                <h3>Preview unavailable</h3>
                <p>Please try again later.</p>
              </EmptyState>
            )}
            
          </div>
        </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default LearnerPublications;
